import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Patch } from 'immer';

import type { NoteSection } from '../types';

/**
 * Represents a single undoable action in the template editor
 */
export interface UndoAction {
  /** Unique ID for this action */
  id: string;
  /** Type of operation performed */
  type: 'add' | 'remove' | 'reorder';
  /** Note ID this action belongs to */
  noteId: string;
  /** Human-readable description of the action */
  description: string;
  /** Timestamp when action was performed */
  timestamp: number;
  /** Immer inverse patches to undo this action */
  inversePatches: Patch[];
  /** Original patches (for redo) */
  patches: Patch[];
  /** Section order after this action (for reorder operations) */
  sectionOrder?: string[];
  /** Section order before this action (for reorder operations) */
  previousSectionOrder?: string[];
  /** Deleted section data (for remove operations) */
  deletedSection?: NoteSection;
  /** Index where section was deleted from (for remove operations) */
  deletedSectionIndex?: number;
  /** Added section module IDs (for add operations) */
  addedModuleIds?: string[];
  /** Index where sections were added (for add operations) */
  addedSectionIndex?: number;
}

/**
 * Template editor undo/redo state
 */
export interface TemplateUndoState {
  /** Stack of past actions (can be undone) */
  past: UndoAction[];
  /** Stack of future actions (can be redone) */
  future: UndoAction[];
  /** Currently performing undo operation */
  isUndoing: boolean;
  /** Currently performing redo operation */
  isRedoing: boolean;
  /** Maximum size of undo stack */
  maxStackSize: number;
}

const initialState: TemplateUndoState = {
  past: [],
  future: [],
  isUndoing: false,
  isRedoing: false,
  maxStackSize: 50,
};

/**
 * Template Undo Slice
 *
 * Manages undo/redo history for template editing operations.
 * Works in conjunction with RTK Query cache updates via Immer patches.
 *
 * Key Principles:
 * - Server is source of truth (no local state duplication)
 * - Optimistic updates via RTK Query updateQueryData
 * - Undo applies inverse patches to cache + calls API
 * - History is session-only (cleared on navigation)
 */
export const templateUndoSlice = createSlice({
  name: 'notes/templateUndo',
  initialState,
  reducers: {
    /**
     * Record a new undoable action
     *
     * This should be called after a successful optimistic update
     * to record the inverse patches for potential undo.
     */
    pushUndoAction: (state, action: PayloadAction<UndoAction>) => {
      // Clear future stack when new action is performed
      state.future = [];

      // Add to past stack
      state.past.push(action.payload);

      // Enforce max stack size
      if (state.past.length > state.maxStackSize) {
        state.past.shift(); // Remove oldest entry
      }
    },

    /**
     * Perform undo operation
     *
     * Moves the last action from past to future stack.
     * The actual cache update is handled by the component.
     */
    performUndo: state => {
      if (state.past.length === 0) return;

      const action = state.past.pop();
      if (action) {
        state.future.unshift(action);
        state.isUndoing = true;
      }
    },

    /**
     * Perform redo operation
     *
     * Moves the first action from future to past stack.
     * The actual cache update is handled by the component.
     */
    performRedo: state => {
      if (state.future.length === 0) return;

      const action = state.future.shift();
      if (action) {
        state.past.push(action);
        state.isRedoing = true;
      }
    },

    /**
     * Mark undo operation as complete
     */
    finishUndo: state => {
      state.isUndoing = false;
    },

    /**
     * Mark redo operation as complete
     */
    finishRedo: state => {
      state.isRedoing = false;
    },

    /**
     * Clear all undo/redo history
     *
     * Called when:
     * - Navigating to different note
     * - User explicitly clears history
     * - Note is deleted
     */
    clearHistory: state => {
      state.past = [];
      state.future = [];
      state.isUndoing = false;
      state.isRedoing = false;
    },

    /**
     * Clear history for specific note
     */
    clearHistoryForNote: (state, action: PayloadAction<string>) => {
      const noteId = action.payload;
      state.past = state.past.filter(item => item.noteId !== noteId);
      state.future = state.future.filter(item => item.noteId !== noteId);
    },
  },
});

export const {
  pushUndoAction,
  performUndo,
  performRedo,
  finishUndo,
  finishRedo,
  clearHistory,
  clearHistoryForNote,
} = templateUndoSlice.actions;

export default templateUndoSlice.reducer;
